/**
 * Copie collegate: lo stesso file caricato una volta e pubblicato in piu' obblighi.
 *
 * Ogni copia e' un documento **dell'obbligo che la riceve** — nome ANAC,
 * indirizzo, durata e archivio di quell'obbligo —, e l'API le lega con un
 * identificativo comune. Qui stanno le regole del back-office: quali obblighi
 * si possono scegliere, come si compone la richiesta, che cosa resta da
 * pubblicare, quando «Sostituisci in tutti» si puo' offrire. Come le altre
 * regole, stanno in `lib/` perche' le verifiche le raggiungano.
 */

import type {
  CopiaCollegata,
  Documento,
  IdNodo,
  Nodo,
  Pubblicazione,
  RichiestaCopia,
} from "../api/types";
import { leggiDurata, nodiFoglia } from "./scadenze";
import type { SceltaDurata } from "./scadenze";

/** Un obbligo scelto per la copia, con la durata come la tiene il modulo. */
export interface SceltaCopia {
  id_nodo: IdNodo;
  scelta: SceltaDurata;
  finoAl: string;
}

/**
 * Gli obblighi che possono ricevere una copia: le stesse foglie che si
 * possono scegliere come principale, tranne il principale stesso.
 */
export function obblighiCandidati(
  nodi: readonly Nodo[],
  selezionabile: (nodo: Nodo) => boolean,
  idPrincipale: IdNodo | null,
): Nodo[] {
  return nodiFoglia(nodi).filter(
    (nodo) => selezionabile(nodo) && nodo.id_nodo !== idPrincipale,
  );
}

/**
 * Filtra i candidati per testo, sul **percorso** e non sulla sola
 * denominazione: fra i 121 obblighi cinquantadue denominazioni si ripetono, e
 * cercare «Atti generali» deve trovare anche la sezione da cui l'obbligo
 * discende.
 */
export function filtraObblighi(
  candidati: readonly Nodo[],
  testo: string,
  percorsoDi: (idNodo: IdNodo) => string,
): Nodo[] {
  const cercato = testo.trim().toLocaleLowerCase("it");
  if (!cercato) return [...candidati];
  return candidati.filter((nodo) =>
    percorsoDi(nodo.id_nodo).toLocaleLowerCase("it").includes(cercato),
  );
}

/**
 * Il campo `copie` della richiesta, o il primo errore da mostrare.
 *
 * Ogni copia porta la propria durata: vale la stessa lettura del documento
 * principale (`leggiDurata`), e un errore su una copia dice in quale obbligo.
 */
export function corpoCopie(
  scelte: readonly SceltaCopia[],
  denominazioneDi: (idNodo: IdNodo) => string,
): { copie: RichiestaCopia[]; errore: string | null } {
  const copie: RichiestaCopia[] = [];
  for (const scelta of scelte) {
    const durata = leggiDurata(scelta.scelta, scelta.finoAl);
    if (durata.errore) {
      return { copie: [], errore: `Copia in «${denominazioneDi(scelta.id_nodo)}»: ${durata.errore}` };
    }
    copie.push({ id_nodo: scelta.id_nodo, durata_anni: durata.durata_anni, fino_al: durata.fino_al });
  }
  return { copie, errore: null };
}

/** Una pubblicazione da pubblicare in un obbligo, per «Pubblica in tutti gli obblighi». */
export interface VoceDaPubblicare {
  pubblicazione_id: number;
  id_nodo: IdNodo;
  denominazione: string;
}

/**
 * Che cosa pubblicare per mettere online una pubblicazione **e** le copie dei
 * suoi documenti: la pubblicazione stessa per prima, poi quella di ogni copia
 * che ha una versione di lavoro, una volta sola anche se piu' documenti
 * hanno copie nello stesso obbligo.
 */
export function daPubblicareInTutti(
  pubblicazione: Pick<Pubblicazione, "id" | "nodo_id" | "documenti">,
  denominazione: string,
): VoceDaPubblicare[] {
  const voci: VoceDaPubblicare[] = [
    { pubblicazione_id: pubblicazione.id, id_nodo: pubblicazione.nodo_id ?? "", denominazione },
  ];
  const viste = new Set([pubblicazione.id]);
  for (const documento of pubblicazione.documenti ?? []) {
    for (const copia of documento.copie_collegate ?? []) {
      if (!copia.da_pubblicare || viste.has(copia.pubblicazione_id)) continue;
      viste.add(copia.pubblicazione_id);
      voci.push({
        pubblicazione_id: copia.pubblicazione_id,
        id_nodo: copia.id_nodo,
        denominazione: copia.denominazione,
      });
    }
  }
  return voci;
}

/**
 * Vero se «Sostituisci anche nelle copie» si puo' offrire.
 *
 * Una copia archiviata non si sostituisce, e l'API rifiuta allora l'intera
 * sostituzione: offrire la casella significherebbe proporre un gesto che
 * finisce in un errore.
 */
export function sostituibiliInTutti(copie: readonly CopiaCollegata[]): boolean {
  return copie.length > 0 && copie.every((copia) => copia.stato !== "archiviato");
}

/** «Pubblicato anche in: X (in bozza), Y» — l'elenco delle copie in una riga. */
export function testoCopie(copie: readonly CopiaCollegata[]): string {
  return copie
    .map((copia) =>
      copia.stato === "pubblicato"
        ? copia.denominazione
        : `${copia.denominazione} (${copia.stato === "in_bozza" ? "in bozza" : "archiviata"})`,
    )
    .join(", ");
}

/**
 * Le copie di ciascun documento pubblicato, ritrovate per indirizzo.
 *
 * La tabella di «Documenti e cartelle» legge la vista pubblica, che le copie
 * non le porta — il legame non e' un'informazione pubblica —, mentre la vista
 * del redattore le porta. Le due righe dello stesso documento hanno lo stesso
 * `url`: e' l'aggancio.
 */
export function copiePerIndirizzo(
  pubblicazioni: readonly Pick<Pubblicazione, "documenti">[],
): ReadonlyMap<string, CopiaCollegata[]> {
  const indice = new Map<string, CopiaCollegata[]>();
  for (const pubblicazione of pubblicazioni) {
    for (const documento of pubblicazione.documenti ?? []) {
      if (documento.url && documento.copie_collegate?.length) {
        indice.set(documento.url, documento.copie_collegate);
      }
    }
  }
  return indice;
}

/** Le copie di un documento, dalla risposta o dall'indice per indirizzo. */
export function copieDi(
  documento: Pick<Documento, "url" | "copie_collegate">,
  indice: ReadonlyMap<string, CopiaCollegata[]>,
): CopiaCollegata[] {
  if (documento.copie_collegate?.length) return documento.copie_collegate;
  return documento.url ? indice.get(documento.url) ?? [] : [];
}
